import { SecurityContext, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot
} from '@angular/router';

import { AuthGuard, AuthService } from '@auth0/auth0-angular';
import { differenceInMinutes } from 'date-fns';
import posthog from 'posthog-js';
import { catchError, filter, map, of, switchMap, take, tap } from 'rxjs';

import { AxleToastService } from 'src/app/shared/services/toast/axle-toast.service';
import { UserService } from 'src/app/shared/services/user/user.service';
import { UserStore } from 'src/app/shared/services/user/user.state';
import { isLoginPrompt } from 'src/app/shared/state/authentication/authentication.methods';
import { environment } from 'src/environments/environment';

export const parentAuthGuardFn: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const auth0Service = inject(AuthService);
  const authGuard = inject(AuthGuard);
  const sanitizer = inject(DomSanitizer);
  const userStore = inject(UserStore);
  const userService = inject(UserService);
  const toast = inject(AxleToastService);
  const userInfo = toObservable(userStore.userInfo);

  // Check for IDP-initiated flow in progress
  const isIdpFlow = sessionStorage.getItem('idpInitiatedFlow') === 'true';

  return auth0Service.isLoading$.pipe(
    filter((loading) => !loading),
    take(1),
    switchMap(() => auth0Service.isAuthenticated$),
    take(1),
    switchMap((authenticated) => {
      // Allow IDP-initiated flow through even if not yet authenticated
      // Auth0 is still processing the callback
      if (!authenticated && !isIdpFlow) {
        throw new Error('unauthenticated');
      }

      // If IDP flow and not authenticated, wait for Auth0 to complete
      if (!authenticated && isIdpFlow) {
        return auth0Service.isAuthenticated$.pipe(
          filter((isAuth) => isAuth),
          take(1),
          switchMap(() => {
            // Clear the flag once authenticated
            sessionStorage.removeItem('idpInitiatedFlow');
            return userInfo;
          })
        );
      }

      // Clear the flag if it exists and we're authenticated
      if (isIdpFlow) {
        sessionStorage.removeItem('idpInitiatedFlow');
      }

      return userInfo;
    }),
    switchMap((userInfo) => {
      if (
        userInfo?.timestamp &&
        differenceInMinutes(new Date(), userInfo.timestamp) < 5
      ) {
        return of(true);
      }

      return userService.getUserInfo$().pipe(
        tap((userInfoResponse) => {
          try {
            userStore.setInitialStore(userInfoResponse);
            posthog.identify(userInfoResponse.id, {
              organizationId: userInfoResponse.organization.id,
              environment: environment.name
            });
            posthog.reloadFeatureFlags();

            if (userInfoResponse.organization.pylonEnabled) {
              window.pylon = {
                chat_settings: {
                  app_id: 'ce148b59-27c3-4c1e-bf10-3b93ee7e1475',
                  email: userInfoResponse.loginEmail,
                  name:
                    userInfoResponse.firstName +
                    ' ' +
                    userInfoResponse.lastName,
                  email_hash: userInfoResponse.hashedEmailPylon
                }
              };
            }
          } catch (err) {
            console.error(err);
          }
        })
      );
    }),
    switchMap(() => authGuard.canActivate(route, state)),
    catchError((err) => {
      const returnUrl = window.location.href;
      const url = sanitizer.sanitize(SecurityContext.URL, returnUrl);

      if (url) {
        const splitUrl = extractAfterThirdSlash(url);
        sessionStorage.setItem('returnUrl', splitUrl);
      }

      toast.addError(
        'Your session has expired. Please login to continue using Axle.'
      );

      // If Auth0 still considers the session valid, the failure came from a
      // downstream API. Force a re-auth via prompt=login so redirectIfAuthenticated
      // doesn't bounce the user straight back into the failing route.
      const isAuthError =
        err instanceof Error && err.message === 'unauthenticated';
      router.navigate(
        ['login'],
        isAuthError ? {} : { queryParams: { prompt: 'login' } }
      );
      return of(true);
    }),
    take(1)
  );
};

function extractAfterThirdSlash(url: string) {
  const parts = url.split('/');

  // Check if there are at least four parts (which means at least three slashes)
  if (parts.length < 4) {
    throw new Error('URL does not contain three slashes.');
  }

  // Join the parts back together, starting from the fourth element (index 3)
  return parts.slice(3).join('/');
}

export const determineAuthRedirectTo: CanActivateFn = () => {
  const router = inject(Router);
  const auth0Service = inject(AuthService);

  return auth0Service.isLoading$.pipe(
    filter((loading) => !loading),
    take(1),
    switchMap(() => auth0Service.isAuthenticated$),
    take(1),
    tap((isAuthenticated) => {
      if (isAuthenticated) {
        navigateToReturnUrlOrHome(router);
      } else {
        router.navigate(['login'], { replaceUrl: true });
      }
    }),
    map(() => true)
  );
};

function navigateToReturnUrlOrHome(router: Router) {
  const returnUrl = sessionStorage.getItem('returnUrl');
  sessionStorage.removeItem('returnUrl');

  if (returnUrl) {
    router.navigateByUrl(returnUrl);
  } else {
    router.navigate(['home']);
  }
}

export const determineRedirectTo: CanActivateFn = () => {
  const router = inject(Router);
  const auth0Service = inject(AuthService);

  return auth0Service.isLoading$.pipe(
    filter((loading) => !loading),
    take(1),
    switchMap(() => auth0Service.isAuthenticated$),
    take(1),
    tap((isAuthenticated) =>
      router.navigate([isAuthenticated ? 'home' : 'login'])
    ),
    map(() => true)
  );
};

export const redirectIfAuthenticated: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => {
  const router = inject(Router);
  const auth0Service = inject(AuthService);

  // Allow the login screen when the caller explicitly requested a prompt
  if (isLoginPrompt(route.queryParamMap.get('prompt'))) {
    return true;
  }

  return auth0Service.isLoading$.pipe(
    filter((loading) => !loading),
    take(1),
    switchMap(() => auth0Service.isAuthenticated$),
    take(1),
    map((isAuthenticated) => {
      if (!isAuthenticated) {
        return true;
      }
      const returnUrl = sessionStorage.getItem('returnUrl');
      sessionStorage.removeItem('returnUrl');
      return router.parseUrl(returnUrl || '/home');
    })
  );
};

export const handleInvitationSignup: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => {
  const invitation = route.queryParamMap.get('invitation');
  const organization = route.queryParamMap.get('organization');

  if (invitation && organization) {
    const authService = inject(AuthService);

    authService
      .loginWithRedirect({
        authorizationParams: {
          invitation,
          organization,
          prompt: 'login'
        }
      })
      .pipe(take(1))
      .subscribe({});

    // Return false to prevent navigation to login component
    return false;
  }

  // No invitation params, allow normal login flow
  return true;
};

export const redirectToAxleAccessDomain: CanActivateFn = () => {
  const hostname = window.location.hostname;

  // Check if we're on a non-axleaccess.com domain (excluding localhost and dev environments)
  // TODO - Chris: Remove once dev is removed/tested
  const envs = [
    'dev',
    'piper',
    'staging',
    'prod',
    'secondary',
    'sandbox',
    'integrations'
  ];
  if (
    envs.includes(environment.env) &&
    !hostname.includes('axleaccess.com') &&
    // TODO - Chris: Remove once dev is removed/tested
    !hostname.includes('localhost') &&
    !hostname.includes('127.0.0.1') &&
    !hostname.includes('.amplifyapp.com')
  ) {
    let subdomain = 'app';
    switch (environment.env) {
      case 'prod':
        subdomain = 'app';
        break;
      case 'secondary':
        subdomain = 'axle-secondary';
        break;
      case 'sandbox':
        subdomain = 'sandbox';
        break;
      case 'integrations':
        subdomain = 'integrations';
        break;
      case 'dev':
        subdomain = 'axle-dev';
        break;
      case 'staging':
        subdomain = 'testing';
        break;
      case 'piper':
        subdomain = 'axle-dev-piper';
        break;
    }
    // Always redirect to app.axleaccess.com
    const targetDomain = `https://${subdomain}.axleaccess.com`;

    // Preserve the path, query params, and hash
    const path = window.location.pathname;
    const search = window.location.search;
    const hash = window.location.hash;

    // Perform the redirect
    window.location.href = `${targetDomain}${path}${search}${hash}`;

    // Return false to prevent navigation on current domain
    return false;
  }

  // Allow navigation on axleaccess.com or local environments
  return true;
};
